import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatCurrency, formatNumber } from "@/utils/format";

import { CostBreakdown } from "./calculateCost";
import { Eye } from "lucide-react";

interface StatsCardsProps {
  totalViews: number;
  totalVideos: number;
  uniqueCreatorsCount: number;
  cost: number;
  predefinedCpm: number;
  hasChallengeCampaigns: boolean;
  hasNonChallengeCampaigns: boolean;
  costBreakdown: CostBreakdown;
}

export function StatsCards({
  totalViews,
  totalVideos,
  uniqueCreatorsCount,
  cost,
  predefinedCpm,
  hasChallengeCampaigns,
  hasNonChallengeCampaigns,
  costBreakdown,
}: StatsCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
      {/* Total Views Card */}
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-primary/60">Total Views</h3>
          <Eye className="h-5 w-5 text-primary/60" />
        </div>
        <p className="text-3xl font-semibold text-primary">
          {formatNumber(totalViews)}
        </p>
      </div>

      {/* Total Videos Posted Card */}
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-primary/60">
            Total Videos Posted
          </h3>
        </div>
        <p className="text-3xl font-semibold text-primary">
          {formatNumber(totalVideos)}
        </p>
      </div>

      {/* Total Unique Creators Card */}
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-primary/60">
            Total Unique Creators
          </h3>
        </div>
        <p className="text-3xl font-semibold text-primary">
          {formatNumber(uniqueCreatorsCount)}
        </p>
      </div>

      {/* Cost Card */}
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-primary/60">Cost</h3>
        </div>
        <p className="text-3xl font-semibold text-primary">
          {formatCurrency(cost)}
        </p>
        {hasChallengeCampaigns &&
          !hasNonChallengeCampaigns &&
          predefinedCpm > 0 && (
            <p className="text-xs text-primary/60 mt-2">
              CPM: {formatCurrency(predefinedCpm)}
            </p>
          )}
        {(hasNonChallengeCampaigns ||
          (hasChallengeCampaigns && hasNonChallengeCampaigns)) && (
          <>
            <p className="text-xs text-primary/60 mt-2">
              Based on contract terms
              {hasChallengeCampaigns &&
                hasNonChallengeCampaigns &&
                " (mixed campaign types)"}
            </p>
            {(costBreakdown.flatFeePerVideo > 0 ||
              costBreakdown.cpmCost > 0 ||
              costBreakdown.timePeriodCost > 0) && (
              <Accordion type="single" collapsible className="mt-3">
                <AccordionItem value="cost-breakdown" className="border-0">
                  <AccordionTrigger className="text-xs text-primary/60 py-2 hover:no-underline">
                    View cost breakdown
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    <div className="space-y-2 text-xs">
                      {costBreakdown.flatFeePerVideo > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-primary/60">
                            Flat fee per video:
                          </span>
                          <span className="font-medium text-primary">
                            {formatCurrency(costBreakdown.flatFeePerVideo)}
                          </span>
                        </div>
                      )}
                      {costBreakdown.cpmCost > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-primary/60">CPM costs:</span>
                          <span className="font-medium text-primary">
                            {formatCurrency(costBreakdown.cpmCost)}
                          </span>
                        </div>
                      )}
                      {costBreakdown.timePeriodCost > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-primary/60">
                            Retainer payments:
                          </span>
                          <span className="font-medium text-primary">
                            {formatCurrency(costBreakdown.timePeriodCost)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-border">
                        <span className="font-semibold text-primary">
                          Total:
                        </span>
                        <span className="font-semibold text-primary">
                          {formatCurrency(costBreakdown.total)}
                        </span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </>
        )}
      </div>
    </div>
  );
}
